<template>
    <v-chart
        :option="monthlyChartData"
        theme="dark"
        ref="monthlyChart"
        style="
            width: 95%;
            height: 200px;
            margin-bottom: 10px;
            margin-left: auto;
            margin-right: auto;
        "
    />
</template>
<script>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import {
    TitleComponent, TooltipComponent, LegendComponent, GridComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import { monthOptions } from '@/assets/commonData/options';

use([CanvasRenderer, PieChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent]);

export default {
    components: {
        VChart,
    },
    props: {
        monthlyData: {
            type: Object,
            default: () => {},
        },
        mode: {
            type: Number,
            default: 0,
        },
    },
    watch: {
        monthlyData(newValue) {
            if (newValue !== null) {
                this.updateCharts();
            }
        },
        mode(newValue) {
            this.updateCharts();
        },
    },
    data() {
        return {
            monthlyChartData: {
                tooltip: {
                    trigger: 'axis',
                },
                backgroundColor: 'transparent',
                grid: {
                    top: 30,
                    left: 0,
                    bottom: 0,
                    right: 15,
                    containLabel: true,
                },
                legend: {
                    data: [],
                    selected: {},
                },
                dataset: { source: [] },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: _.map(monthOptions, 'value'),
                },
                yAxis: {
                    type: 'value',
                    axisLine: {
                        show: false,
                    },
                    splitLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.2)',
                            width: 1,
                            type: 'solid',
                        },
                    },
                },
                series: [],
            },
        };
    },
    mounted() {
        window.addEventListener('resize', this.updateChartSize);
    },
    destroyed() {
        window.removeEventListener('resize', this.updateChartSize);
    },
    methods: {
        updateChartSize() {
            this.$refs.monthlyChart.resize();
        },
        setupChartOptionData() {
            if (!this.monthlyData) {
                return { legendData: [], selected: {}, series: [] };
            }

            const incomes = [];
            let incomeSum = 0;
            this.monthlyData.income?.forEach((item, i) => {
                // eslint-disable-next-line max-len
                const ACTUAL = Math.round(item) - Math.round(this.monthlyData.creditNoteNoTax[i]);
                if (!this.mode) incomes.push(ACTUAL);
                else {
                    incomeSum += ACTUAL;
                    incomes.push(incomeSum);
                }
            });
            const payouts = [];
            let payoutSum = 0;
            this.monthlyData.payout?.forEach((item) => {
                if (!this.mode) payouts.push(Math.round(item));
                else {
                    payoutSum += Math.round(item);
                    payouts.push(payoutSum);
                }
            });
            const netIncomes = [];
            let netIncomeSum = 0;
            this.monthlyData.netIncome?.forEach((item, i) => {
                // eslint-disable-next-line max-len
                const ACTUAL = Math.round(item) - Math.round(this.monthlyData.creditNoteNoTax[i]);
                if (!this.mode) netIncomes.push(ACTUAL);
                else {
                    netIncomeSum += ACTUAL;
                    netIncomes.push(netIncomeSum);
                }
            });

            const incomeText = this.mode ? '營業總收入累計' : '單月營業總收入';
            const payoutText = this.mode ? '營業總支出累計' : '單月營業總支出';
            const netIncomeText = this.mode ? '營業淨收累計' : '單月營業淨收';
            const depositText = '存款餘額';
            const loanText = '借款餘額';
            const selected = {
                [depositText]: false,
                [loanText]: false,
            };

            const legendData = [incomeText, payoutText, netIncomeText];
            const series = [
                {
                    name: incomeText,
                    type: 'line',
                    animation: false,
                    areaStyle: {},
                    data: incomes,
                    itemStyle: {
                        normal: {
                            color: '#00cc00',
                        },
                    },
                },
                {
                    name: payoutText,
                    type: 'line',
                    animation: false,
                    areaStyle: {},
                    data: payouts,
                    itemStyle: {
                        normal: {
                            color: '#ff9d00',
                        },
                    },
                },
                {
                    name: netIncomeText,
                    type: 'line',
                    animation: false,
                    data: netIncomes,
                    itemStyle: {
                        normal: {
                            color: '#ffffff',
                        },
                    },
                },
            ];

            const deposits = [];
            this.monthlyData.monthlyDeposit?.forEach((item) => {
                deposits.push(Math.round(item));
            });

            if (deposits.length) {
                legendData.push(depositText);
                series.push({
                    name: depositText,
                    type: 'line',
                    show: false,
                    animation: false,
                    areaStyle: {},
                    data: deposits,
                    itemStyle: {
                        normal: {
                            color: '#00bbff',
                        },
                    },
                });
            }

            const loans = [];
            this.monthlyData.monthlyLoan?.forEach((item) => {
                loans.push(Math.round(item));
            });

            if (loans.length) {
                legendData.push(loanText);
                series.push({
                    name: loanText,
                    type: 'line',
                    show: false,
                    animation: false,
                    data: loans,
                    itemStyle: {
                        normal: {
                            color: '#ff00a2',
                        },
                    },
                });
            }

            return { legendData, selected, series };
        },
        updateMonthlyChart() {
            const { legendData, selected, series } = this.setupChartOptionData();

            const returnChartOption = {
                tooltip: {
                    trigger: 'axis',
                },
                backgroundColor: 'transparent',
                grid: {
                    top: 60,
                    left: 0,
                    bottom: 0,
                    right: 15,
                    containLabel: true,
                },
                legend: {
                    data: legendData,
                    selected,
                },
                dataset: { source: [] },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: _.map(monthOptions, 'value'),
                },
                yAxis: {
                    type: 'value',
                    axisLine: {
                        show: false,
                    },
                    splitLine: {
                        lineStyle: {
                            color: 'rgba(255,255,255,0.2)',
                            width: 1,
                            type: 'solid',
                        },
                    },
                },
                series,
            };

            // 曲線圖重載
            this.$refs.monthlyChart.clear();
            this.monthlyChartData = returnChartOption;
        },
        updateCharts() {
            this.updateMonthlyChart();
        },
    },
};
</script>
